"use client";

import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import {
  Box,
  Image,
  Text,
  Flex,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  Tag,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormLabel,
  Tooltip,
} from "@chakra-ui/react";
import { ChevronDownIcon, DownloadIcon, EditIcon, CalendarIcon, CheckIcon, CloseIcon, DeleteIcon } from "@chakra-ui/icons";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import CommonCheckTable from "common/components/checktable";
import CommonDeleteModel from "common/components/commonDeleteModel";
import { HasAccess } from "common/utils/accessUtils";
import { accessModuleTitles } from "common/constant";
import { globalStyles } from "common/theme/styles";
import DateFormatter from "common/utils/dateFormater";
import viewIcon from "common/assets/img/viewIcon.png";
import editIcon from "common/assets/img/editIcon.png";
import offerLetterService from "services/Recruit/services/offerLetter";
import { getStatusBadgeStyle } from "common/utils/statusColor";
import RevokeOfferModal from "services/Recruit/components/RevokeOfferModal";
import { offerStatusOptions } from "services/Recruit/data/dropdownData";
import { getApi } from "common/services/api";
import { API_PATHS } from "common/services/apiPaths";
import { recruitConstant } from "services/Recruit/constant";
import InfoModal from "common/components/infoModel";
import CustomDatePicker from "common/components/datePicker/CustomDatePicker";
import {
  getTableFilters,
  saveTableFilters,
  clearTableFilters,
  getInitialFilterState
} from "common/utils/filterUtils";
import { formatStatusOrStage } from "common/utils/textFormatters";
import { IoWarningOutline } from "react-icons/io5";

const Offers = () => {
  const title = "Offer Letters";
  const TABLE_NAME = 'offers'; // Define table name constant
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([]);
  const [selectedValues, setSelectedValues] = useState([]);
  const [deleteMany, setDeleteMany] = useState(false);
  const [displaySearchData, setDisplaySearchData] = useState(false);
  const [searchedData, setSearchedData] = useState([]);
  const colors = globalStyles.colors;
  const navigate = useNavigate();
  const location = useLocation();
  const [sortColumn, setSortColumn] = useState(null);
  const [sortOrder, setSortOrder] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchbox, setSearchbox] = useState(() => {
    const filters = getInitialFilterState(TABLE_NAME);
    return filters?.searchbox || "";
  });

  const [menuSelectedDeleteItem, setMenuSelectedDeleteItem] = useState([]);
  // State to track current status filter
  const [currentStatusFilter, setCurrentStatusFilter] = useState(() => {
    const filters = getInitialFilterState(TABLE_NAME);
    return filters?.currentStatusFilter || "";
  });

  // Save filters whenever they change
  useEffect(() => {
    const filters = {
      currentStatusFilter,
      searchbox: searchbox || "",
    };
    saveTableFilters(TABLE_NAME, filters);
  }, [currentStatusFilter, searchbox]);
  const [finalSearchedData, setFinalSearchedData] = useState([]);
  const [metadata, setMetadata] = useState({});
  const [permission] = HasAccess([accessModuleTitles.Offers]);
  const [revokeModal, setRevokeModal] = useState({
    isOpen: false,
    offerId: null,
  });

  const [application, setApplication] = useState([]);
  const [isApplicationLoading, setIsApplicationLoading] = useState(true);

  const [candidateData, setCandidateData] = useState([]);
  const [isRevoking, setIsRevoking] = useState(false);
  const [editExpiryModal, setEditExpiryModal] = useState({
    isOpen: false,
    offerId: null,
    currentExpiryDate: null,
    currentJoiningDate: null,
  });
  const [newExpiryDate, setNewExpiryDate] = useState(null);
  const [newJoiningDate, setNewJoiningDate] = useState(null);
  const [isUpdatingExpiry, setIsUpdatingExpiry] = useState(false);

  // Handler for status filter change
  const handleStatusFilterChange = useCallback(
    (selectedStatus) => {
      // Update current status filter state
      setCurrentStatusFilter(selectedStatus);
      setCurrentPage(1);
    },
    []
  );

  // Fetch data when filters or pageSize change
  useEffect(() => {
    // When filters change, reset to page 1 and fetch
    fetchData(
      1, // Always start from page 1 when filters change
      pageSize,
      sortColumn,
      sortOrder,
      searchbox || "",
      currentStatusFilter || ""
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pageSize, // Page size change
    currentStatusFilter, // Status filter change
    sortColumn, // Sort column change
    sortOrder, // Sort order change
  ]);

  const fetchData = async (
    page = currentPage,
    limit = pageSize,
    sortKey = sortColumn,
    order = sortOrder,
    searchTerm = "",
    status = ""
  ) => {
    setIsLoading(true);

    // Build params compatible with offerLetterService.getOfferLetters
    const params = { page, limit };

    if (sortKey) {
      params.sort = order === "desc" ? `-${sortKey}` : sortKey;
    }
    if (searchTerm && searchTerm.trim() !== "") {
      params.search = searchTerm.trim();
    }
    if (status && status.trim() !== "") {
      // Normalize status to lowercase for consistency with backend
      const normalizedStatus = status.toLowerCase().trim();
      params.status = normalizedStatus;
    }

    try {
      const response = await offerLetterService.getOfferLetters(params);
      // The service returns response.data, which contains { data: [], metadata: {} }
      const resultData = response?.data ?? [];
      const resultMeta = response?.metadata ?? {};

      const baseIndex = (page - 1) * limit;
      const formattedData = (resultData || []).map((item, index) => ({
        _id: item._id,
        srNo: baseIndex + index + 1, // continuous numbering across pages
        customId: item.customId || "-",
        candidateName: item.candidate?.name || "-",
        jobTitle: item.job?.postingTitle || "-",
        salary:
          item.salary?.amount != null
            ? `₹${item.salary.amount.toLocaleString("en-IN")}`
            : "-",
        joiningDate: item.joiningDate
          ? DateFormatter({ date: item.joiningDate })
          : "-",
        expiryDate: item.offerExpiryDate
          ? DateFormatter({ date: item.offerExpiryDate })
          : "-",
        status: item.status,
        sentAt: item.sentAt ? DateFormatter({ date: item.sentAt }) : "-",
        createdBy:
          `${item.createBy?.firstName || ""} ${item.createBy?.lastName || ""
            }`.trim() || "-",
        createdAt: DateFormatter({ date: item.createdAt }),
        rawData: item,
        application: item.application,
        candidate: item.candidate,
      }));
      setData(formattedData);
      setMetadata(resultMeta);
    } catch (error) {
      toast.error("Failed to fetch offer letters");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };
  // NEW: Fetch applications on mount
  useEffect(() => {
    const fetchApplications = async () => {
      try {
        const result = await getApi("api/recruitment/application/?allData=true");
        setApplication(result?.data?.data || []);
      } catch (e) {
        console.error("Failed to fetch job openings:", e);
        toast.error("Failed to load job openings");
      } finally {
        setIsApplicationLoading(false);
      }
    };
    const fetchCandidateData = async () => {
      let endpoint = `${API_PATHS.CANDIDATE_LIST}?allData=true`;
      try {
        const result = await getApi(endpoint);
        setCandidateData(result?.data?.data);
      } catch (e) {
        toast.error(recruitConstant.errorMsg);
        console.error("Error fetching job openings:", e);
      }
    };
    fetchApplications();
    fetchCandidateData();
  }, []);

  const [deleteInfo, setDeleteInfo] = useState({
    isVisible: false,
    msg: "",
  });
  const handleNavigateToCandidate = useCallback((candidateId, mode = "overview") => {

    const candidateExists = candidateData.some(candidate => candidate._id === candidateId);

    if (!candidateExists) {
      setDeleteInfo({ isVisible: true, msg: recruitConstant.jobOpeningDeleteMsg.replace('application', 'offer').replace('job opening', 'candidate') });

      return;
    }

    sessionStorage.setItem("mode", mode);
    navigate(`/recruitment/candidate-create/${candidateId}`);
  }, [candidateData, navigate]);
  const handleNavigateToOffer = useCallback(
    (values, mode = "overview", type = "navigate") => {
      if (isApplicationLoading) {
        toast.info("Please wait, loading data...");
        return;
      }

      const applicationExists = application.some(
        (app) => app._id === values?.application?._id
      );

      if (!applicationExists) {
        setDeleteInfo({
          isVisible: true,
          msg: recruitConstant.jobOpeningDeleteMsg
            .replace("application", "offer")
            .replace("job opening", "application"),
        });
        return false
      }

      if (type !== 'navigate')
        return true

      sessionStorage.setItem("mode", mode);
      navigate(`/recruitment/offerLetterCreate/${values?._id}`);


    },
    [isApplicationLoading, application, navigate]
  );
  const onLinkClick = (row) => {

    return handleNavigateToOffer(row, "overview", "navigate");
  };
  const onRowClick = (row) => {
    return handleNavigateToOffer(row?.original, "overview", "just-check");
  };


  const handleDownloadPDF = async (id) => {
    try {
      const blob = await offerLetterService.downloadOfferPDF(id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `offer-letter-${id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded successfully");
    } catch (error) {
      toast.error("Failed to download PDF");
      console.error("PDF Download error:", error);
    }
  };

  const handleRelease = async (id) => {
    try {
      await offerLetterService.releaseOfferLetter(id);
      toast.success("Offer letter released successfully");
      fetchData(currentPage, pageSize, sortColumn, sortOrder, searchbox, currentStatusFilter);
    } catch (error) {
      toast.error("Failed to release offer letter");
      console.error(error);
    }
  };

  const handleEditExpiry = (id, currentExpiryDate, currentJoiningDate) => {
    setEditExpiryModal({ isOpen: true, offerId: id, currentExpiryDate, currentJoiningDate });
    setNewExpiryDate(currentExpiryDate ? new Date(currentExpiryDate) : null);
    setNewJoiningDate(currentJoiningDate ? new Date(currentJoiningDate) : null);
  };

  const handleUpdateExpiry = async () => {
    if (!editExpiryModal.offerId || !newExpiryDate) {
      toast.error("Please select a valid expiry date");
      return;
    }

    setIsUpdatingExpiry(true);
    try {
      await offerLetterService.updateOfferExpiryDate(editExpiryModal.offerId, newExpiryDate, newJoiningDate);
      await offerLetterService.releaseOfferLetter(editExpiryModal.offerId);
      toast.success("Offer letter re-released successfully with new expiry and joining dates");
      setEditExpiryModal({ isOpen: false, offerId: null, currentExpiryDate: null, currentJoiningDate: null });
      setNewExpiryDate(null);
      setNewJoiningDate(null);
      fetchData(currentPage, pageSize, sortColumn, sortOrder, searchbox, currentStatusFilter);
    } catch (error) {
      toast.error("Failed to re-release offer letter");
      console.error(error);
    } finally {
      setIsUpdatingExpiry(false);
    }
  };

  const handleRevoke = (id) => {
    setRevokeModal({ isOpen: true, offerId: id });
  };

  const handleRevokeConfirm = async (reason) => {
    if (!revokeModal.offerId) return;

    setIsRevoking(true);
    try {
      await offerLetterService.revokeOfferLetter(
        revokeModal.offerId,
        reason
      );
      toast.success("Offer letter revoked successfully");
      setRevokeModal({ isOpen: false, offerId: null });
      fetchData(currentPage, pageSize, sortColumn, sortOrder, searchbox, currentStatusFilter);
    } catch (error) {
      toast.error("Failed to revoke offer letter");
      console.error(error);
    } finally {
      setIsRevoking(false);
    }
  };

  const handleDeleteOffers = async (ids) => {
    try {
      await offerLetterService.deleteManyOfferLetters(ids);
      toast.success("Offer letter deleted successfully");
      setSelectedValues([]);
      setDeleteMany(false);
      fetchData(currentPage, pageSize, sortColumn, sortOrder, searchbox, currentStatusFilter);
    } catch (error) {
      toast.error("Failed to withdraw offer letter");
      console.error(error);
    } finally {
      setMenuSelectedDeleteItem([]);
    }
  };

  const actionHeader = {
    Header: "Action",
    isSortable: false,
    center: true,
    cell: ({ row }) => {
      const status = row.original.status;
      const canRelease = ["draft", "approved"].includes(status);
      const isExpired = status === "expired";
      const canRevoke = !["declined", "revoked"].includes(status);
      let isEditAllow = true
      const applicationExists = application.some(
        (app) => app._id === row.original?.application?._id
      );

      if (!applicationExists) {

        isEditAllow = false
      }

      return (
        <Flex justify="center" align="center" w="100%">
          <Menu>
            <MenuButton
              as={IconButton}
              minW={0}
              padding={1}
              backgroundColor={"transparent"}
              size="sm"
              variant="ghost"
              onClick={(e) => e.stopPropagation()}
              fontWeight="bold"
              fontSize="xl"
              color="gray.600"
              _hover={{ bg: "gray.100" }}
            >
              ⋮
            </MenuButton>
            <MenuList minW="150px" shadow="md">
              {isEditAllow && permission?.update && status === "draft" && (
                <MenuItem
                  icon={<EditIcon />}
                  onClick={() => {
                    sessionStorage.setItem("mode", "edit");
                    navigate(`/recruitment/offerLetterCreate/${row.original._id}`);
                  }}
                >
                  Edit
                </MenuItem>
              )}
              {status === "accepted" && (
                <MenuItem
                  icon={<DownloadIcon />}
                  onClick={(e) => {
                    e.stopPropagation()
                    handleDownloadPDF(row.original._id)
                  }}
                >
                  Download PDF
                </MenuItem>
              )}
              {isExpired && permission?.update && (
                <MenuItem
                  isDisabled={!isEditAllow}
                  icon={<CalendarIcon />}
                  onClick={() => handleEditExpiry(row.original._id, row.original.rawData?.offerExpiryDate, row.original.rawData?.joiningDate)}
                >
                  Edit Expiry Date
                </MenuItem>
              )}
              {canRelease && permission?.update && (
                <MenuItem
                  isDisabled={!isEditAllow}
                  icon={<CheckIcon />}
                  onClick={() => handleRelease(row.original._id)}
                >
                  Release Offer
                </MenuItem>
              )}
              {canRevoke && permission?.update && (
                <MenuItem
                  isDisabled={!isEditAllow}
                  icon={<CloseIcon />}
                  onClick={() => handleRevoke(row.original._id)}
                >
                  Revoke Offer
                </MenuItem>
              )}
              {permission?.delete && (
                <MenuItem
                  color="red.500"
                  icon={<DeleteIcon color="red.500" />}
                  onClick={(e) => {
                    e.stopPropagation()
                    setDeleteMany(true);
                    setSelectedValues([row.original._id]);
                    setMenuSelectedDeleteItem([row?.values?._id]);

                  }}
                >
                  Delete
                </MenuItem>
              )}
            </MenuList>
          </Menu>
        </Flex>
      );
    },
  };

  const tableColumns = useMemo(() => {
    return [
      {
        Header: "#",
        accessor: "_id",
        isSortable: false,
        width: 50,

      },
      {
        Header: "ID",
        accessor: "customId",
        cell: ({ value, row }) => {
          const applicationDeleted = row?.original?.application?.deleted;
          return (
            <Box display="flex" alignItems="center" gap={2}>
              <Text
                cursor={"pointer"}
                onClick={() => {
                  handleNavigateToOffer(row?.original, "overview", "navigate");
                }}
              >
                {value}
              </Text>
              {applicationDeleted && (
                // <Tag size="sm" bgColor={'#3C2FC0'} color={'white'} variant="outline">
                //   Application Deleted
                // </Tag>

                <Tooltip label="Application Deleted" hasArrow placement="right">
                  <span style={{ display: "inline-flex", alignItems: "center" }}>
                    <IoWarningOutline className="overdue-icon" />
                  </span>
                </Tooltip>
              )}
            </Box>
          );
        },
      },
      {
        Header: "Candidate",
        accessor: "candidateName",
        cell: ({ value, row }) => {
          return <Text cursor={"pointer"} onClick={() => {
            const candidateId = row?.original?.candidate?._id

            if (candidateId) {
              handleNavigateToCandidate(candidateId, "overview", "navigate")
            }
          }}>
            {value || "-"}
          </Text>
        },
      },
      {
        Header: "Job Title",
        accessor: "jobTitle",
        cell: ({ value, row }) => {
          let formattedTitle = value || "";

          // If title starts with "Other-", remove the prefix and format the rest
          if (formattedTitle.startsWith("Other-")) {
            formattedTitle = formattedTitle
              .substring(6) // Remove "Other-"
              .replace(/-/g, " ")
              .split(" ")
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
              .join(" ");
          } else {
            // For standard titles, format normally
            formattedTitle = formattedTitle
              .replace(/-/g, " ")
              .split(" ")
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
              .join(" ");
          }

          return <Text cursor={"pointer"}
            onClick={() => {
              handleNavigateToOffer(row?.original, "overview", "navigate");
            }}>{value ? formattedTitle : "-"}</Text>;
        },
      },
      {
        Header: "Salary",
        headerLabel: "Salary (Per Annum)",
        accessor: "salary",
        cell: ({ value, row }) => {
          return <Text cursor={"pointer"}
            onClick={() => {
              handleNavigateToOffer(row?.original, "overview", "navigate");
            }}>{value}</Text>;
        },
      },
      {
        Header: "Joining Date",
        accessor: "joiningDate",
        cell: ({ value, row }) => {
          return <Text cursor={"pointer"}
            onClick={() => {
              handleNavigateToOffer(row?.original, "overview", "navigate");
            }}>{value}</Text>;
        },
      },
      {
        Header: "Expiry Date",
        accessor: "expiryDate",
        cell: ({ value, row }) => {
          return <Text cursor={"pointer"}
            onClick={() => {
              handleNavigateToOffer(row?.original, "overview", "navigate");
            }}>{value}</Text>;
        },
      },
      {
        Header: "Status",
        accessor: "status",
        cell: ({ value, row }) => {
          const badgeStyle = getStatusBadgeStyle(value);
          return (
            <Box display="flex" alignItems="center" gap={2}>
              <Box bg={badgeStyle.color} h="8px" w="8px" borderRadius="full" />
              <Text as="span" color={badgeStyle.color} style={{ color: badgeStyle.color }} fontWeight="500" whiteSpace="nowrap">
                {formatStatusOrStage(value)}
              </Text>
            </Box>
          );
        },
      },
      {
        Header: "Created By",
        accessor: "createdBy",
        cell: ({ value, row }) => {
          const formattedString = value.replace(".", " ");
          return <Text cursor={"pointer"}
            onClick={() => {
              handleNavigateToOffer(row?.original, "overview", "navigate");
            }}>{formattedString}</Text>;
        },
      },
      ...(permission?.update || permission?.view || permission?.delete
        ? [actionHeader]
        : []),
    ]
  }, [handleNavigateToOffer, actionHeader, permission]);


  const handleSearchChange = (term) => {
    const trimmed = term?.trim() ?? "";
    const pageToFetch = trimmed === "" ? currentPage : 1;
    if (trimmed !== "" && currentPage !== 1) {
      setCurrentPage(1);
    }
    setSearchbox(trimmed);
    fetchData(pageToFetch, pageSize, sortColumn, sortOrder, trimmed, currentStatusFilter);
  };

  const handleSortChange = (accessorKey) => {
    let newSortOrder = "asc";
    if (sortColumn === accessorKey) {
      newSortOrder = sortOrder === "asc" ? "desc" : "asc";
    }
    setSortColumn(accessorKey);
    setSortOrder(newSortOrder);
    fetchData(currentPage, pageSize, accessorKey, newSortOrder, searchbox, currentStatusFilter);
  };

  const [columns] = useState([...tableColumns]);
  const isInitialMount = useRef(true);
  const defaultVisibleHeaders = [
    "ID",
    "Candidate",
    "Job Title",
    "Status",
    "Salary",
    "Joining Date",
    "Expiry Date",
    "Action",
  ];
  const defaultVisibleColumns = defaultVisibleHeaders
    .map((header) => tableColumns.find((column) => column.Header === header))
    .filter(Boolean);
  const [selectedColumns, setSelectedColumns] = useState([...defaultVisibleColumns])
  useEffect(() => {
    if (isInitialMount.current && tableColumns.length > 0) {
      setSelectedColumns([...defaultVisibleColumns]);
      isInitialMount.current = false;
    }
  }, [tableColumns, defaultVisibleColumns]);
  const dataColumn = selectedColumns.filter((item) =>
    columns.some((column) => column.Header === item.Header)
  );
  function closeDeleteModal() {
    setMenuSelectedDeleteItem([]);
    setDeleteMany(false)
  }
  return (
    <div>
      <CommonCheckTable
        title={title}
        showInlineTitle={true}
        isLoading={isLoading}
        columnData={columns}
        dataColumn={dataColumn}
        allData={data}
        tableData={data}
        onLinkClick={onLinkClick}
        onRowClick={onRowClick}
        setSearchDisplay={setDisplaySearchData}
        tableCustomFields={[]}
        access={permission}
        selectedColumns={selectedColumns}
        setSelectedColumns={setSelectedColumns}
        selectedValues={selectedValues}
        setSelectedValues={setSelectedValues}
        setDelete={setDeleteMany}
        onOpen={() => navigate("/recruitment/offerLetterCreate")}
        sortOrder={sortOrder}
        searchbox={searchbox}
        handleSortChange={handleSortChange}
        handleSearchChange={handleSearchChange}
        setSearchbox={setSearchbox}
        filterStatusOptions={offerStatusOptions}
        statusFilter={true}
        handleStatusFilterChange={handleStatusFilterChange}
        currentStatusFilter={currentStatusFilter}
        finalSearchedData={finalSearchedData}
        metadata={metadata}
        setFinalSearchedData={setFinalSearchedData}
        setCurrentPage={setCurrentPage}
        fetchData={fetchData}
        currentPage={currentPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
        isPagination={true}
        isDynamic={true}
      />


      <CommonDeleteModel
        isOpen={deleteMany}
        onClose={() => closeDeleteModal()}
        type="Offer Letters"
        handleDeleteData={handleDeleteOffers}
        ids={selectedValues}
        onCancel={() => setSelectedValues([])}
      />

      <RevokeOfferModal
        isOpen={revokeModal.isOpen}
        onClose={() => setRevokeModal({ isOpen: false, offerId: null })}
        onConfirm={handleRevokeConfirm}
        isLoading={isRevoking}
      />
      <InfoModal
        onClose={() => setDeleteInfo({ isVisible: false, msg: '' })}
        isOpen={deleteInfo.isVisible}
        message={deleteInfo.msg}
      />

      <Modal
        isOpen={editExpiryModal.isOpen}
        onClose={() => {
          setEditExpiryModal({ isOpen: false, offerId: null, currentDate: null });
          setNewExpiryDate(null);
        }}
        size="xl"
      >
        <ModalOverlay />
        <ModalContent minH="300px">
          <ModalHeader>Edit Offer Expiry Date</ModalHeader>
          <ModalBody>
            <Box mb={4}>
              <FormLabel>New Expiry Date</FormLabel>
              <CustomDatePicker
                fullWidth={true}
                startDate={newExpiryDate}
                placeholder="Select new expiry date"
                setStartDate={setNewExpiryDate}
                minDate={new Date()}
              />
            </Box>
            <Box>
              <FormLabel>New Joining Date</FormLabel>
              <CustomDatePicker
                fullWidth={true}
                startDate={newJoiningDate}
                placeholder="Select new joining date"
                setStartDate={setNewJoiningDate}
                minDate={newExpiryDate || new Date()}
              />
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={() => {
                setEditExpiryModal({ isOpen: false, offerId: null, currentExpiryDate: null, currentJoiningDate: null });
                setNewExpiryDate(null);
                setNewJoiningDate(null);
              }}
            >
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleUpdateExpiry}
              isLoading={isUpdatingExpiry}
              isDisabled={!newExpiryDate}
            >
              Update & Re-release
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default Offers;
